import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { ArrowRight } from "lucide-react";

import appCss from "../styles.css?inline";
import interLatin from "../assets/fonts/inter-latin-wght-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SITE_NAME, SITE_URL, THEME_COLOR } from "@/lib/site";
import { HASH_REDIRECT_SCRIPT, DEFERRED_GTAG_SCRIPT } from "@/lib/hash-redirects";
import { SiteHeader } from "@/components/site/header";
import { SiteFooter } from "@/components/site/footer";
import { HashRedirect } from "@/components/site/hash-redirect";
import { Eyebrow } from "@/components/site/eyebrow";

function NotFoundComponent() {
  useEffect(() => {
    document.title = `Page not found | ${SITE_NAME}`;
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) robots.setAttribute("content", "noindex, follow");
  }, []);

  return (
    <main id="main" tabIndex={-1} className="relative overflow-hidden py-20 sm:py-28 lg:py-32">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-56 -top-48 size-[34rem] rounded-full bg-rose-tint/40 blur-3xl"
      />
      <div className="relative mx-auto max-w-2xl px-5 text-center">
        <Eyebrow>PAGE NOT FOUND</Eyebrow>
        <h1 className="mt-6 text-4xl leading-[1.08] sm:text-5xl">
          This page isn't{" "}
          <em className="accent-underline font-display italic text-primary">here.</em>
        </h1>
        <p className="mt-6 text-base leading-relaxed text-muted-foreground">
          The page you're looking for may have moved. Try the collection, or head home to start
          again.
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/"
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-soft)] transition-transform hover:-translate-y-0.5"
          >
            Go home
          </Link>
          <Link
            to="/collection"
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-primary/50 px-7 py-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
          >
            Browse the Collection <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </main>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div id="main" className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex min-h-11 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, viewport-fit=cover",
      },
      { name: "author", content: SITE_NAME },
      { name: "theme-color", content: THEME_COLOR },
      { name: "color-scheme", content: "light" },
      { name: "format-detection", content: "telephone=yes, address=yes, email=yes" },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { name: "googlebot", content: "index, follow" },
      { name: "google-site-verification", content: "bTk0VYknn2AFW3vli3KHJpFUFHrO825Sej9gFybfLK4" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "geo.region", content: "US-MI" },
      { name: "geo.placename", content: "Midland" },
    ],
    links: [
      // Preload only the body font. Fraunces is display-only and uses
      // font-display: optional below so it stays off the LCP critical path
      // (it was the longest Network dependency tree chain at ~45 KiB).
      {
        rel: "preload",
        href: interLatin,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "describedby", href: `${SITE_URL}/llms.txt` },
    ],
    styles: [
      {
        children: appCss,
      },
    ],
    scripts: [
      {
        children: HASH_REDIRECT_SCRIPT,
      },
      {
        children: DEFERRED_GTAG_SCRIPT,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en-US">
      <head>
        <HeadContent />
      </head>
      <body>
        <a href="#main" className="skip-link">
          Skip to content
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const studio = pathname === "/studio" || pathname.startsWith("/studio/");

  return (
    <div className="min-h-screen bg-background">
      <HashRedirect />
      {studio ? null : <SiteHeader />}
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      {studio ? null : <SiteFooter />}
    </div>
  );
}
